import React, { useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface Feedback {
  id: string;
  content: string;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

const AdminFeedback: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/feedback/all`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch feedback');
        }

        const data = await response.json();
        setFeedbacks(data);
      } catch (err) {
        setError('Failed to load feedback. Please try again.');
        console.error('Error fetching feedback:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeedback();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  return (
    <div className="section">
      <div className="wrap" style={{ maxWidth: 980 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
          Internal · admin
        </span>
        <h1 style={{ fontSize: 30, marginBottom: 8 }}>User feedback</h1>
        <p className="meta" style={{ marginBottom: 24 }}>
          Everything users have submitted through the feedback form.
        </p>

        {error && (
          <div className="note" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="card" style={{ textAlign: 'center', padding: 40 }}>
            <p className="meta">Loading feedback…</p>
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px 28px' }}>
            <h3 style={{ marginBottom: 8 }}>No feedback yet</h3>
            <p className="meta">User feedback will appear here once submitted.</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="data" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Feedback</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((feedback) => (
                  <tr key={feedback.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{feedback.user.name}</div>
                      <div className="meta">{feedback.user.email}</div>
                    </td>
                    <td style={{ maxWidth: 480 }}>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                        {feedback.content}
                      </div>
                    </td>
                    <td className="meta" style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(feedback.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedback;
